import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, FastForward, Search } from 'lucide-react';
import { Button } from '../../common/Button';
import { inventoryService } from '../../../services/inventoryService';
import { cn } from '../../../utils/cn';
import type { GreenBeanPickerItem } from './types';

interface Props {
  onContinue: (selected: GreenBeanPickerItem[]) => void;
}

export const PickGreenBeansStep: React.FC<Props> = ({ onContinue }) => {
  const [items, setItems] = useState<GreenBeanPickerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    inventoryService
      .listGreenBeansForRBImportPicker()
      .then((data) => {
        if (!cancelled) {
          setItems(data);
          setError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (g) => g.code.toLowerCase().includes(q) || g.name.toLowerCase().includes(q),
    );
  }, [items, search]);

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((g) => selected.has(g.id));

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filtered.forEach((g) => next.delete(g.id));
      } else {
        filtered.forEach((g) => next.add(g.id));
      }
      return next;
    });
  };

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleContinue = () => {
    const chosen = items.filter((g) => selected.has(g.id));
    onContinue(chosen);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Pick the green beans you want to import roasted bulk for — the template
        will pre-fill one starter row per selected bean. Already have a filled
        template? <span className="font-medium text-gray-700">Skip</span> straight
        to upload.
      </p>

      <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5">
        <Search className="h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by code or name…"
          className="flex-1 bg-transparent text-sm outline-none"
          data-test-id="import-rb-pick-search"
        />
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Failed to load green beans: {error}
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="max-h-[55vh] overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50 shadow-[0_1px_0_0_rgb(229_231_235)]">
              <tr>
                <th className="w-10 px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleAll}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    data-test-id="import-rb-pick-toggle-all"
                  />
                </th>
                <th className="px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Code
                </th>
                <th className="px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Name
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr>
                  <td colSpan={3} className="px-3 py-6 text-center text-gray-400">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-3 py-6 text-center text-gray-400">
                    No green beans match.
                  </td>
                </tr>
              )}
              {filtered.map((g) => {
                const isSel = selected.has(g.id);
                return (
                  <tr key={g.id} className={cn(isSel && 'bg-primary-50/30')}>
                    <td className="px-3 py-1.5">
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={() => toggle(g.id)}
                        data-test-id={`import-rb-pick-row-${g.code}`}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-2 py-1.5 font-mono text-xs text-gray-700">{g.code}</td>
                    <td className="px-2 py-1.5 text-gray-900">{g.name}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100">
        <span className="text-xs text-gray-500" data-test-id="import-rb-pick-count">
          {selected.size} selected
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={() => onContinue([])}
            leftIcon={<FastForward className="h-4 w-4" />}
            data-test-id="import-rb-pick-skip"
          >
            Skip 
          </Button>
          <Button
            variant="primary"
            disabled={selected.size === 0}
            onClick={handleContinue}
            rightIcon={<ArrowRight className="h-4 w-4" />}
            data-test-id="import-rb-pick-continue"
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
};
